import React from 'react';
import { Link } from 'react-router-dom';
import { FaGithub, FaLinkedin, FaTwitter, FaWhatsapp } from 'react-icons/fa';
import { projects, skills, cv } from '../Data';
import profileImg from '../assets/yogesh-singh-frontend-developer.png';
import SEO from '../components/seo/SEO';
import { SITE_URL } from '../config/seo';
import './yogesh-singh.css';

const pageTitle =
  'Yogesh Singh – Frontend Developer, React & Next.js | Gurgaon, India';
const pageDescription =
  'Yogesh Singh is a Frontend Developer and React developer based in Gurgaon, India. 4+ years building JavaScript, React, and Next.js apps, dashboards, and e-commerce. View skills, experience, and projects.';

export default function YogeshSinghPage() {
  const experience = cv.filter((c) => c.category === 'experience');
  const education = cv.filter((c) => c.category === 'education');

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        canonicalPath="/yogesh-singh"
        ogType="profile"
      />

      <article className="yogesh-page">
        <div className="yogesh-page__hero container">
          <p className="yogesh-page__breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true"> / </span>
            <span>Yogesh Singh</span>
          </p>

          <div className="yogesh-page__hero-grid">
            <div>
              <h1 className="yogesh-page__h1">Yogesh Singh</h1>
              <p className="yogesh-page__tagline">
                Frontend Developer specializing in React, Next.js, and JavaScript — based in Gurgaon,
                India.
              </p>
              <p className="yogesh-page__lede">
                I build fast, accessible interfaces and production-ready web apps. Companies hire me
                as a React developer when they need strong Next.js SEO, dashboards, or real-time
                classroom and booking flows.
              </p>
              <div className="yogesh-page__socials">
                <a href="https://www.linkedin.com/in/yogesh1dev" target="_blank" rel="noopener noreferrer" aria-label="Yogesh Singh on LinkedIn">
                  <FaLinkedin />
                </a>
                <a href="https://github.com/yogeshmlv" target="_blank" rel="noopener noreferrer" aria-label="Yogesh Singh on GitHub">
                  <FaGithub />
                </a>
                <a href="https://twitter.com/Yogesh__Dev" target="_blank" rel="noopener noreferrer" aria-label="Yogesh Singh on Twitter">
                  <FaTwitter />
                </a>
                <a href="https://wa.link/br0lsr" target="_blank" rel="noopener noreferrer" aria-label="Contact Yogesh Singh on WhatsApp">
                  <FaWhatsapp />
                </a>
              </div>
              <p className="yogesh-page__back">
                <Link to="/#home">← Back to portfolio home</Link>
              </p>
            </div>
            <div className="yogesh-page__photo-wrap">
              <img
                src={profileImg}
                alt="Yogesh Singh — Frontend Developer and React developer in Gurgaon, India"
                className="yogesh-page__photo"
                width={440}
                height={440}
                decoding="async"
                fetchpriority="high"
              />
            </div>
          </div>
        </div>

        <div className="yogesh-page__section container">
          <h2 className="yogesh-page__h2">Full bio</h2>
          <div className="yogesh-page__prose">
            <p>
              I am <strong>Yogesh Singh</strong>, a <strong>Frontend Developer</strong> with over four
              years of professional experience. I work from <strong>Gurgaon, India</strong>, and
              collaborate with teams building products in EdTech, healthcare, e-commerce, and SaaS.
            </p>
            <p>
              My core stack is <strong>React</strong>, <strong>Next.js</strong>, and modern{' '}
              <strong>JavaScript</strong> (including TypeScript where it helps teams ship safely). I
              care about performance, Core Web Vitals, and clean component architecture—so users get
              pages that load quickly and stay easy to maintain.
            </p>
            <p>
              If you are searching for a <strong>Yogesh Singh React developer</strong> or{' '}
              <strong>Yogesh Singh Next.js</strong> engineer, this page summarizes how I work: product
              sense, attention to detail, and shipping code that matches design and business goals.
            </p>
          </div>
        </div>

        <div className="yogesh-page__section container">
          <h2 className="yogesh-page__h2">Skills</h2>
          <p className="yogesh-page__intro">
            Selected skills—aligned with how teams describe a senior <strong>frontend</strong> role.
          </p>
          <ul className="yogesh-page__skills">
            {skills.map((s) => (
              <li key={s.id}>
                <h3 className="yogesh-page__h3">{s.name}</h3>
                <p>{s.description}</p>
              </li>
            ))}
          </ul>
        </div>

        <div className="yogesh-page__section container">
          <h2 className="yogesh-page__h2">Experience</h2>
          <ul className="yogesh-page__timeline">
            {experience.map((item) => (
              <li key={item.id}>
                <h3 className="yogesh-page__h3">{item.title}</h3>
                <p className="yogesh-page__meta">{item.subtitle}</p>
                <p className="yogesh-page__meta">{item.date}</p>
                <p>{item.description}</p>
                {item.techStack && (
                  <p>
                    <strong>Tech:</strong> {item.techStack}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="yogesh-page__section container">
          <h2 className="yogesh-page__h2">Projects</h2>
          <p className="yogesh-page__intro">
            Representative work—many built as lead <strong>React</strong> or <strong>Next.js</strong>{' '}
            frontend.
          </p>
          <ul className="yogesh-page__projects">
            {projects.map((p) => (
              <li key={p.id}>
                <h3 className="yogesh-page__h3">
                  <a href={p.linkUrl} target="_blank" rel="noopener noreferrer">
                    {p.title}
                  </a>
                </h3>
                <p>{p.description}</p>
                {p.techStack && <p className="yogesh-page__meta">{p.techStack}</p>}
              </li>
            ))}
          </ul>
        </div>

        <div className="yogesh-page__section container">
          <h2 className="yogesh-page__h2">Education</h2>
          <ul className="yogesh-page__timeline">
            {education.map((item) => (
              <li key={item.id}>
                <h3 className="yogesh-page__h3">{item.title}</h3>
                <p className="yogesh-page__meta">{item.subtitle}</p>
                <p className="yogesh-page__meta">{item.date}</p>
              </li>
            ))}
          </ul>
        </div>

        <div className="yogesh-page__section container yogesh-page__section--cta">
          <h2 className="yogesh-page__h2">Connect</h2>
          <p>
            Portfolio:{' '}
            <a href={SITE_URL} rel="noopener noreferrer">
              {SITE_URL}
            </a>
          </p>
          <p>
            <Link to="/#contact">Contact Yogesh Singh via the portfolio form</Link>
          </p>
        </div>
      </article>
    </>
  );
}
